import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Exercise } from '@/models/LearnTypes';
import { cn } from '@/lib/utils';
import BaseExercise from './BaseExercise';
import { usePerfectLevel } from '@/contexts/PerfectLevelContext';

// Define animation for the connection lines
const drawLineKeyframes = `
@keyframes draw-line {
  0% {
    stroke-dashoffset: 1000;
  }
  100% {
    stroke-dashoffset: 0;
  }
}
`;

// Add the animation style to the document
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = drawLineKeyframes;
  document.head.appendChild(style);
}

interface MatchExerciseProps {
  currentExercise: Exercise;
  exercises: Exercise[];
  isAnimating: boolean;
  onNext: () => void;
  isLastExercise: boolean;
  onComplete?: (wordsToReview: string[]) => void;
  initialAnimation?: boolean;
  onClose?: () => void;
  isPerfect?: boolean;
  onImperfect?: () => void;
  onLogActivity?: (activityType: string, exercise: Exercise) => void;
}

const MatchExercise: React.FC<MatchExerciseProps> = ({
  currentExercise,
  exercises,
  isAnimating,
  onNext,
  isLastExercise,
  onComplete,
  initialAnimation = false,
  onClose,
  isPerfect,
  onImperfect,
  onLogActivity
}) => {
  // Shuffle the words and translations once when component mounts
  const [words, setWords] = useState<{ id: string; text: string; matched: boolean }[]>([]);
  const [translations, setTranslations] = useState<{ id: string; text: string; matched: boolean }[]>([]);
  
  // Track the active selection
  const [activeSelection, setActiveSelection] = useState<{
    type: 'word' | 'translation';
    id: string;
  } | null>(null);
  
  // Track completed matches
  const [matches, setMatches] = useState<{ wordId: string; translationId: string }[]>([]);
  
  // Track incorrect match animation
  const [incorrectMatch, setIncorrectMatch] = useState<{
    wordId: string;
    translationId: string;
  } | null>(null);
  
  // Track if any mistakes have been made in this exercise
  const [hadMistake, setHadMistake] = useState(false);

  // Debug counter for any external calls to onImperfect
  const imperfectCallCounter = useRef(0);
  
  // Initialize the lists when the component mounts or when exercises change
  useEffect(() => {
    console.debug('🔎 [MatchExercise] Initializing with isPerfect prop:', isPerfect);
    console.debug('🔎 [MatchExercise] Setting initial hadMistake to false');
    // Create and shuffle the words
    const shuffledWords = exercises.map(exercise => ({
      id: exercise.id,
      text: exercise.word,
      matched: false
    })).sort(() => Math.random() - 0.5);
    
    // Create and shuffle the translations (independently from words)
    const shuffledTranslations = exercises.map(exercise => ({
      id: exercise.id,
      text: exercise.translation,
      matched: false
    })).sort(() => Math.random() - 0.5);
    
    setWords(shuffledWords);
    setTranslations(shuffledTranslations);
    setMatches([]);
    setActiveSelection(null);
    setIncorrectMatch(null);
    setHadMistake(false); // Reset mistake tracking on component mount/exercises change
    
    // CRITICAL: Reset global state variables to ensure clean state
    // This is vital to prevent flag persistence between exercise sessions
    console.debug('🧹 [MatchExercise] INITIALIZING: Resetting all global flags and localStorage');
    
    // Get current level index for level-specific flags
    let levelIndex = null;
    try {
      const pathname = window.location.pathname;
      const match = pathname.match(/\/learn\/[^\/]+\/([0-9]+)/);
      if (match && match[1]) {
        levelIndex = parseInt(match[1], 10);
        console.debug(`🧹 [MatchExercise] Detected current level index: ${levelIndex}`);
      }
    } catch (e) {
      console.error('Error getting level index from URL:', e);
    }
    
    // Reset window global flags
    (window as any).__MATCH_EXERCISE_HAD_MISTAKE__ = false;
    (window as any).__MATCH_EXERCISE_IS_PERFECT__ = true;
    
    // Reset localStorage flags - make sure to reset BOTH global and level-specific flags
    try {
      localStorage.setItem('match_exercise_had_mistake', 'false');
      
      // Also reset the level-specific flag if we have a level index
      if (levelIndex !== null) {
        localStorage.setItem(`match_exercise_had_mistake_level_${levelIndex}`, 'false');
        console.debug(`🧹 [MatchExercise] Reset level-specific flag: match_exercise_had_mistake_level_${levelIndex}=false`);
      } else {
        console.debug(`⚠️ [MatchExercise] Couldn't determine level index from URL for level-specific flag reset`);
      }
    } catch (e) {
      console.error('Error resetting localStorage flags:', e);
    }
    // Only reset the mistake flags if this is a fresh exercise start (not a re-render)
    if (exercises.length > 0) {
      console.debug('🔄 [MatchExercise] FRESH EXERCISE START - Resetting all mistake flags');
      
      (window as any).__MATCH_EXERCISE_HAD_MISTAKE__ = false;
      (window as any).__MATCH_EXERCISE_IS_PERFECT__ = true; // Start with perfect state
      (window as any).__PERFECT_LEVEL_STATUS__ = true; // Also reset global level status
      
      // Also reset localStorage for persistent state tracking
      try {
        // IMPORTANT: Use setItem, not removeItem to ensure proper values
        localStorage.setItem('match_exercise_had_mistake', 'false');
        
        // Reset the level-specific key if available
        const levelIndex = window.location.pathname.includes('/learn/') ? 
          window.location.pathname.split('/').pop() : null;
        if (levelIndex && !isNaN(Number(levelIndex))) {
          localStorage.setItem(`match_exercise_had_mistake_level_${levelIndex}`, 'false');
          console.debug(`🔄 [MatchExercise] Reset level-specific mistake flag: match_exercise_had_mistake_level_${levelIndex}=false`);
        }
        
        console.debug('🔄 [MatchExercise] RESET global state and localStorage at initialization');
      } catch (e) {
        console.error('Error resetting localStorage:', e);
      }
    }
    
    // Reset the imperfect call counter
    imperfectCallCounter.current = 0;
    
    console.debug('🔎 [MatchExercise] COMPLETE RESET: All state and flags initialized for a new exercise');
  }, [exercises]);

  // Update states when a match is made
  const updateMatchStates = (wordId: string, translationId: string) => {
    // Update words list
    setWords(prev => prev.map(word => 
      word.id === wordId ? { ...word, matched: true } : word
    ));
    
    // Update translations list
    setTranslations(prev => prev.map(translation => 
      translation.id === translationId ? { ...translation, matched: true } : translation
    ));
    
    // Add to matches
    setMatches(prev => [...prev, { wordId, translationId }]);
  };

  // Handle click on a word
  const handleWordClick = (wordId: string) => {
    // If word is already matched, do nothing
    if (words.find(w => w.id === wordId)?.matched) return;
    
    // If there's already an active word, change selection to this word
    if (activeSelection?.type === 'word') {
      setActiveSelection({ type: 'word', id: wordId });
      return;
    }
    
    // If there's an active translation, check if it's a match
    if (activeSelection?.type === 'translation') {
      if (activeSelection.id === wordId) {
        // Match found
        updateMatchStates(wordId, activeSelection.id);
        setActiveSelection(null);
      } else {
        // No match - record the incorrect match for UI feedback
        setIncorrectMatch({ wordId, translationId: activeSelection.id });
        
        // Log the specific incorrect match details for debugging
        console.debug(`🚨 [MatchExercise] INVALID MATCH DETECTED! Word=${wordId}, translation=${activeSelection.id}`);
        
        // Consistently track the mistake across all state layers using our enhanced function
        // This will update React state, global variables, localStorage, and call parent handlers
        handleImperfect();
        
        // Reset after animation
        setTimeout(() => {
          setIncorrectMatch(null);
          setActiveSelection(null);
        }, 800);
      }
      return;
    }
    
    // Otherwise, set this word as active
    setActiveSelection({ type: 'word', id: wordId });
  };

  // Handle click on a translation
  const handleTranslationClick = (translationId: string) => {
    // If translation is already matched, do nothing
    if (translations.find(t => t.id === translationId)?.matched) return;
    
    // If there's already an active translation, change selection to this translation
    if (activeSelection?.type === 'translation') {
      setActiveSelection({ type: 'translation', id: translationId });
      return;
    }
    
    // If there's an active word, check if it's a match
    if (activeSelection?.type === 'word') {
      if (activeSelection.id === translationId) {
        // Match found
        updateMatchStates(activeSelection.id, translationId);
        setActiveSelection(null);
      } else {
        // No match - record the incorrect match for UI feedback
        setIncorrectMatch({ wordId: activeSelection.id, translationId });
        
        // Log the specific incorrect match details for debugging
        console.debug(`🚨 [MatchExercise] INVALID MATCH DETECTED! Word=${activeSelection.id}, translation=${translationId}`);
        
        // Consistently track the mistake across all state layers using our enhanced function
        // This will update React state, global variables, localStorage, and call parent handlers
        handleImperfect();
        
        // Reset after animation
        setTimeout(() => {
          setIncorrectMatch(null);
          setActiveSelection(null);
        }, 800);
      }
      return;
    }
    
    // Otherwise, set this translation as active
    setActiveSelection({ type: 'translation', id: translationId });
  };

  // Check if all matches are completed
  const isComplete = matches.length === exercises.length;

  // Create refs for word and translation elements
  const wordRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const translationRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  
  // Force re-render of connections when matches change
  const [connectionUpdate, setConnectionUpdate] = useState(0);
  useEffect(() => {
    // Small delay to ensure DOM elements are rendered
    const timer = setTimeout(() => setConnectionUpdate(prev => prev + 1), 100);
    return () => clearTimeout(timer);
  }, [matches]);
  
  // Force an update when the window is resized to redraw lines
  useEffect(() => {
    const handleResize = () => setConnectionUpdate(prev => prev + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Function to draw connection lines directly on the DOM after render
  useEffect(() => {
    // Clear any previous connection lines
    document.querySelectorAll('.match-connection-line').forEach(el => el.remove());
    
    // Only proceed if there are matches
    if (matches.length === 0) return;
    
    // Get the card container for reference positioning
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    
    // Create lines for each match
    matches.forEach(({ wordId, translationId }) => {
      const wordEl = wordRefs.current[wordId];
      const translationEl = translationRefs.current[translationId];
      
      if (!wordEl || !translationEl) return;
      
      const wordRect = wordEl.getBoundingClientRect();
      const translationRect = translationEl.getBoundingClientRect();
      
      // Create SVG element for the line
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'match-connection-line');
      svg.style.position = 'absolute';
      svg.style.top = '0';
      svg.style.left = '0';
      svg.style.width = '100%';
      svg.style.height = '100%';
      svg.style.pointerEvents = 'none';
      svg.style.zIndex = '1';
      svg.style.overflow = 'visible';
      
      // Create the line element
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      
      // Calculate the positions for drawing the line from center of word to center of translation
      const wordCenterX = wordRect.left + (wordRect.width / 2) - containerRect.left;
      const wordCenterY = wordRect.top + (wordRect.height / 2) - containerRect.top;
      const translationCenterX = translationRect.left + (translationRect.width / 2) - containerRect.left;
      const translationCenterY = translationRect.top + (translationRect.height / 2) - containerRect.top;
      
      const x1 = wordCenterX + (wordRect.width / 2); // Right side of word box
      const y1 = wordCenterY;
      const x2 = translationCenterX - (translationRect.width / 2); // Left side of translation box
      const y2 = translationCenterY;
      
      line.setAttribute('x1', x1.toString());
      line.setAttribute('y1', y1.toString());
      line.setAttribute('x2', x2.toString());
      line.setAttribute('y2', y2.toString());
      // Style the line
      line.setAttribute('stroke', '#22c55e');
      line.setAttribute('stroke-width', '3');
      line.setAttribute('stroke-linecap', 'round'); 
      
      // Add some curvature for a nicer appearance
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const midX = (x1 + x2) / 2;
      const pathD = `M${x1},${y1} Q${midX},${y1} ${midX},${(y1 + y2) / 2} T${x2},${y2}`;
      path.setAttribute('d', pathD);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', '#22c55e');
      path.setAttribute('stroke-width', '3');
      path.setAttribute('stroke-linecap', 'round');
      
      // Use the path instead of line for a nicer curved connection
      path.classList.add('animate-draw-line');
      
      // Get total length for animation
      svg.appendChild(path);
      const totalLength = path.getTotalLength();
      path.setAttribute('stroke-dasharray', totalLength.toString());
      path.setAttribute('stroke-dashoffset', totalLength.toString());
      
      // Add animation
      path.style.animation = 'draw-line 0.6s ease forwards';
      
      // Remove the path and re-add to ensure the animation is applied
      svg.removeChild(path);
      svg.appendChild(path);
      
      // Add SVG to container
      container.appendChild(svg);
    });
    
    // Cleanup function to remove SVGs when component unmounts
    return () => {
      document.querySelectorAll('.match-connection-line').forEach(el => el.remove());
    };
  }, [matches, connectionUpdate]);
  
  // Dummy function to maintain code structure - we're using the useEffect above instead
  const renderConnections = () => null;

  const renderExerciseContent = () => (
    <div className="space-y-6" ref={containerRef}>
      <div className="p-2 text-center">
        <h3 className="text-xl font-semibold mb-1">Match the words with their translations</h3>
        <p className="text-sm text-muted-foreground mb-2">
          Click a word and then its matching translation to connect them
        </p>
      </div>
      
      <div className="relative flex justify-between px-4 min-h-[300px]">
        {/* Words column */}
        <div className="flex flex-col space-y-3 min-w-[130px] z-20">
          <div className="text-center font-medium text-sm text-muted-foreground mb-1">Words</div>
          {words.map((word) => (
            <div
              key={`word-${word.id}`}
              ref={(el: HTMLDivElement | null) => { if (el) wordRefs.current[word.id] = el; }}
              onClick={() => handleWordClick(word.id)}
              className={cn(
                "p-3 rounded-md border-2 cursor-pointer transition-all duration-200 text-center",
                word.matched && "border-green-500 bg-green-50",
                !word.matched && activeSelection?.type === 'word' && activeSelection.id === word.id && "border-blue-500 bg-blue-50",
                !word.matched && incorrectMatch?.wordId === word.id && "border-red-500 bg-red-50 animate-shake",
                !word.matched && !activeSelection && "border-gray-200 hover:border-gray-300"
              )}
            >
              {word.text}
            </div>
          ))}
        </div>
        
        {/* Space for connections */}
        <div className="flex-grow mx-4 relative">
          {/* Draw connections between matched pairs */}
          {renderConnections()}
        </div>
        
        {/* Translations column */}
        <div className="flex flex-col space-y-3 min-w-[130px] z-20">
          <div className="text-center font-medium text-sm text-muted-foreground">Translations</div>
          {translations.map((translation) => (
            <div
              key={`translation-${translation.id}`}
              ref={(el: HTMLDivElement | null) => { if (el) translationRefs.current[translation.id] = el; }}
              onClick={() => handleTranslationClick(translation.id)}
              className={cn(
                "p-3 rounded-md border-2 cursor-pointer transition-all duration-200 text-center",
                translation.matched && "border-green-500 bg-green-50",
                !translation.matched && activeSelection?.type === 'translation' && activeSelection.id === translation.id && "border-blue-500 bg-blue-50",
                !translation.matched && incorrectMatch?.translationId === translation.id && "border-red-500 bg-red-50 animate-shake",
                !translation.matched && !activeSelection && "border-gray-200 hover:border-gray-300"
              )}
            >
              {translation.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderActionButtons = () => {
    return (
      <Button
        className="w-full mt-4"
        onClick={() => {
          if (isComplete) {
            // Get the current mistake status from all possible sources
            const storedHadMistake = localStorage.getItem('match_exercise_had_mistake') === 'true';
            const globalHadMistake = (window as any).__MATCH_EXERCISE_HAD_MISTAKE__ === true;
            
            // Final hadMistake is true if ANY source indicates a mistake
            const finalHadMistake = hadMistake || storedHadMistake || globalHadMistake || (imperfectCallCounter.current > 0);
            
            console.debug(`✅ [MatchExercise] COMPLETION: Component hadMistake=${hadMistake}`);
            console.debug(`✅ [MatchExercise] COMPLETION: Global hadMistake=${globalHadMistake}`);
            console.debug(`✅ [MatchExercise] COMPLETION: Stored hadMistake=${storedHadMistake}`);
            console.debug(`✅ [MatchExercise] COMPLETION: imperfectCallCount=${imperfectCallCounter.current}`);
            console.debug(`✅ [MatchExercise] COMPLETION: FINAL hadMistake=${finalHadMistake}`);
            console.debug(`✅ [MatchExercise] COMPLETION: Will pass isPerfect=${!finalHadMistake} to parent components`);
            
            // CRUCIAL: Set the global variables with the final determination
            (window as any).__MATCH_EXERCISE_HAD_MISTAKE__ = finalHadMistake;
            (window as any).__MATCH_EXERCISE_IS_PERFECT__ = !finalHadMistake;
            
            // Also update localStorage for persistence
            try {
              localStorage.setItem('match_exercise_had_mistake', String(finalHadMistake));
              localStorage.setItem('match_exercise_is_perfect', String(!finalHadMistake));
            } catch (e) {
              console.error('Error updating localStorage:', e);
            }
            
            // Only pass empty array (no words to review) when complete
            if (onComplete) {
              onComplete([]);
            }
            onNext();
          }
        }}
        disabled={!isComplete}
      >
        {isComplete ? (isLastExercise ? 'Complete Level' : 'Continue') : 'Match all words to continue'}
      </Button>
    );
  };

  // Debug effect for tracking state changes
  useEffect(() => {
    console.debug(`🔎 [MatchExercise] STATE CHANGED: hadMistake=${hadMistake}, isPerfect=${isPerfect}`);
    
    // Check for potential issues: External component might be marking level as imperfect
    if (isPerfect === false && !hadMistake) {
      console.debug('⚠️ [MatchExercise] MISMATCH DETECTED: External isPerfect=false but internal hadMistake=false');
    }
    
    // Log the current match progress
    if (matches.length > 0) {
      console.debug(`🔎 [MatchExercise] Current matches: ${matches.length}/${exercises.length}`);
    }
  }, [hadMistake, isPerfect, matches.length, exercises.length]);

  // Get access to the perfect level context
  const { markLevelImperfect } = usePerfectLevel();

  // Simplified wrapper for marking exercise as imperfect
  const handleImperfect = () => {
    // 1. Update the local React state for UI rendering
    setHadMistake(true);
    
    // 2. Increment the imperfect call counter for tracking multiple mistakes
    imperfectCallCounter.current += 1;
    
    // 3. Get the current level index from the URL
    const levelIndex = window.location.pathname.includes('/learn/') ? 
      window.location.pathname.split('/').pop() : null;
    const levelIndexNumber = levelIndex && !isNaN(Number(levelIndex)) ? Number(levelIndex) : -1;
    
    // 4. Mark the level as imperfect in the central context (if we can determine level index)
    if (levelIndexNumber >= 0) {
      console.log(`❌ [MatchExercise] MISTAKE DETECTED (#${imperfectCallCounter.current}) on level ${levelIndexNumber}`);
      markLevelImperfect(levelIndexNumber);
    }
    
    // 5. Log failed word activity
    if (onLogActivity && activeSelection) {
      // Find the exercise corresponding to the active selection
      const exercise = exercises.find(ex => ex.id === activeSelection.id);
      if (exercise) {
        console.log(`📝 [MatchExercise] Logging failed word activity for word: ${exercise.word}`);
        // Log the failed word activity with both activity type and exercise object
        onLogActivity('failed_word', exercise);
      }
    }
    
    // 6. Call the parent's onImperfect handler (maintaining backward compatibility)
    if (onImperfect) {
      onImperfect();
    } else {
      console.warn(`⚠️ [MatchExercise] No parent onImperfect handler provided`); 
    }
  };

  // SIMPLIFIED MISTAKE DETECTION SYSTEM
  // Get level index from URL
  const levelPath = window.location.pathname.includes('/learn/') ? 
    window.location.pathname.split('/').pop() : null;
  const levelIndex = levelPath && !isNaN(Number(levelPath)) ? Number(levelPath) : -1;
  
  // Check if we've completed all matches
  const completedAllMatches = isComplete && matches.length === exercises.length;
  
  // A level is imperfect if:
  // 1. The component state shows a mistake (hadMistake = true)
  // 2. The onImperfect function was called at least once (imperfectCallCounter > 0)
  // 3. We haven't completed all matches yet (only relevant when rendering the final state)
  const finalHadMistake = hadMistake || 
                          (imperfectCallCounter.current > 0) || 
                          (!completedAllMatches && isComplete);
  
  // Calculate perfection based on this simplified decision
  const isExercisePerfect = !finalHadMistake;
  
  // Concise, focused logging
  console.log(`🔍 [MatchExercise] Level ${levelIndex}: ${isExercisePerfect ? 'Perfect' : 'Imperfect'} (hadMistake=${hadMistake}, calls=${imperfectCallCounter.current}, matches=${matches.length}/${exercises.length})`);
  
  
  // We'll handle state synchronization in a useEffect hook instead of during render
  
  // Simple effect to reconcile internal state with parent's expectations upon completion
  useEffect(() => {
    // Only run when exercise is complete
    if (!isComplete) return;
    
    // Handle mismatches between our state and parent's expectations
    if (isPerfect && finalHadMistake) {
      console.log(`ℹ️ [MatchExercise] State mismatch - we detected mistakes but parent thinks we're perfect`);
      // Call handleImperfect to ensure the parent knows about our mistakes
      if (imperfectCallCounter.current === 0) {
        handleImperfect();
      }
    }
    else if (!isPerfect && !finalHadMistake) {
      // If parent says not perfect but we think it is, trust the parent
      console.log(`ℹ️ [MatchExercise] Adjusting to match parent's non-perfect state`);
      setHadMistake(true);
    }
  }, [isPerfect, finalHadMistake, isComplete, handleImperfect]);

  // Simple completion effect - when the exercise is complete, ensure state is properly finalized
  useEffect(() => {
    // Only run when exercise is complete
    if (!isComplete) return;
    
    console.log(`✅ [MatchExercise] Exercise complete with ${isExercisePerfect ? 'perfect' : 'imperfect'} status`);
    
    // If not perfect and we have a level index, update the context one final time
    if (!isExercisePerfect && levelIndex >= 0) {
      console.log(`✅ [MatchExercise] Ensuring level ${levelIndex} is marked imperfect`);
      markLevelImperfect(levelIndex);
    }
    
    // Cleanup function is much simpler now - no need to manage multiple state layers
    return () => {
      if (isComplete) {
        console.log(`🧹 [MatchExercise] Cleanup: Final state - isPerfect=${isExercisePerfect}`);
      }
    };
  }, [isComplete, isExercisePerfect, levelIndex, markLevelImperfect]);
  
  return (
    <BaseExercise
      currentExercise={currentExercise}
      isAnimating={isAnimating}
      onNext={onNext}
      isLastExercise={isLastExercise}
      renderExerciseContent={renderExerciseContent}
      renderActionButtons={renderActionButtons}
      initialAnimation={initialAnimation}
      onClose={onClose}
      isPerfect={isExercisePerfect} // Use our simplified perfect status calculation
      onImperfect={handleImperfect} // Use wrapper for better debugging
    />
  );
};

export default MatchExercise;
